/**
 * Sprint 5B — Factuurdetail Betalingsopvolging paneel.
 * Toont 3-domein badges + acties + historie.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, RefreshCcw, Send, PauseCircle, PlayCircle, Ban,
  Handshake, History, Zap, AlertTriangle,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { TripleStatusBadges } from "./StatusBadges";
import ReminderPreviewDialog from "./ReminderPreviewDialog";
import ReminderPauseDialog from "./ReminderPauseDialog";

const STAGE_LABELS = {
  pre_reminder: "Pre-reminder (-3d)",
  on_due: "Op vervaldatum",
  overdue_7: "1e herinnering (+7d)",
  overdue_14: "2e herinnering (+14d)",
  escalation_30: "Interne escalatie (+30d)",
};

function fmt(x, currency = "EUR") {
  const s = Number(x || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${currency} ${s}`;
}

export default function FactuurBetalingsopvolgingPanel({ invoiceId, currency = "EUR" }) {
  const { user } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(user?.role);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("hold");

  const load = async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/sales-invoices/${invoiceId}/reminder-status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const handleResume = async () => {
    try {
      await adminApi.post(`/admin/sales-invoices/${invoiceId}/reminder-resume`);
      toast.success("Automatische opvolging hervat");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading || !status) {
    return (
      <div
        className="p-4 border border-gray-200 bg-white flex items-center gap-2 text-gray-500"
        data-testid="factuurdetail-betalingsopvolging-panel-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Betalingsopvolging laden...
      </div>
    );
  }

  const isPaused = ["hold", "gepauzeerd", "betalingsregeling"].includes(status.followup_status);
  const isFinished = ["afgerond"].includes(status.followup_status);
  const isEscalated = status.followup_status === "escalatie";

  return (
    <div
      className="border border-gray-200 bg-white p-4 space-y-4"
      data-testid="factuurdetail-betalingsopvolging-panel"
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-chivo font-bold text-[#0A192F]">Betalingsopvolging</h3>
          <div className="text-[11px] text-gray-500 mt-0.5">
            Alle beslissingen worden 100% backend genomen. Frontend toont.
          </div>
        </div>
        <Button
          variant="outline" size="sm" onClick={load}
          className="rounded-none h-7 text-[10px]"
        >
          <RefreshCcw className="w-3 h-3 mr-1" /> Ververs
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Statussen (3 gescheiden domeinen)
          </div>
          <TripleStatusBadges
            financial_status={status.financial_status}
            due_status={status.due_status}
            days_overdue={status.days_overdue}
            followup_status={status.followup_status}
          />
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Bedragen (backend-gerekend)
          </div>
          <div className="text-xs">
            <div>Totaal: <strong>{fmt(status.total_incl_vat, currency)}</strong></div>
            <div>Betaald: <strong>{fmt(status.total_paid, currency)}</strong></div>
            <div>Openstaand: <strong className="text-red-700">{fmt(status.amount_due, currency)}</strong></div>
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
            Reminder-status
          </div>
          <div className="text-xs">
            <div>Laatste: <strong>{status.last_reminder_stage_sent
              ? STAGE_LABELS[status.last_reminder_stage_sent] || status.last_reminder_stage_sent
              : "geen"}</strong></div>
            <div>Laatste-datum: {status.last_reminder_sent_at
              ? new Date(status.last_reminder_sent_at).toLocaleDateString("nl-NL")
              : "—"}</div>
            <div>Automation: <strong className={status.followup_status === "actief" ? "text-emerald-700" : "text-gray-500"}>
              {status.followup_status === "actief" ? "actief" : "gepauzeerd"}
            </strong></div>
          </div>
        </div>
      </div>

      {isEscalated && (
        <div
          className="border-l-4 border-red-600 bg-red-50 p-2 text-xs text-red-900"
          data-testid="factuurdetail-escalation-banner"
        >
          <AlertTriangle className="inline w-3.5 h-3.5 mr-1" />
          Deze factuur is <strong>geescaleerd</strong> — er worden geen
          automatische klantmails meer verzonden. Handmatige beoordeling vereist.
        </div>
      )}

      {status.followup_pause_reason && (
        <div
          className="border-l-4 border-amber-500 bg-amber-50 p-2 text-xs text-amber-900"
          data-testid="factuurdetail-pause-banner"
        >
          <strong>Opvolging gepauzeerd</strong> ({status.followup_pause_reason})
          {status.followup_paused_by && <> · door {status.followup_paused_by}</>}
          {status.followup_paused_at && (
            <> · op {new Date(status.followup_paused_at).toLocaleDateString("nl-NL")}</>
          )}
        </div>
      )}

      {/* Acties */}
      {canManage && !isFinished && (
        <div className="flex flex-wrap gap-2">
          {!isPaused && !isEscalated && (
            <Button
              size="sm"
              onClick={() => setPreviewOpen(true)}
              className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="reminder-send-single-btn"
            >
              <Send className="w-3.5 h-3.5 mr-1" /> Reminder nu versturen
            </Button>
          )}
          {!isPaused && (
            <>
              <Button
                size="sm" variant="outline"
                onClick={() => { setPauseReason("hold"); setPauseOpen(true); }}
                className="rounded-none h-8"
                data-testid="reminder-hold-btn"
              >
                <Ban className="w-3.5 h-3.5 mr-1" /> Op hold zetten
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => { setPauseReason("payment_plan"); setPauseOpen(true); }}
                className="rounded-none h-8"
                data-testid="reminder-payment-plan-btn"
              >
                <Handshake className="w-3.5 h-3.5 mr-1" /> Betalingsregeling activeren
              </Button>
              <Button
                size="sm" variant="outline"
                onClick={() => { setPauseReason("manual"); setPauseOpen(true); }}
                className="rounded-none h-8"
                data-testid="reminder-pause-btn"
              >
                <PauseCircle className="w-3.5 h-3.5 mr-1" /> Manueel pauzeren
              </Button>
            </>
          )}
          {isPaused && (
            <Button
              size="sm"
              onClick={handleResume}
              className="rounded-none h-8 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="reminder-resume-btn"
            >
              <PlayCircle className="w-3.5 h-3.5 mr-1" /> Automatische opvolging hervatten
            </Button>
          )}
        </div>
      )}

      {isFinished && (
        <div
          className="border-l-4 border-emerald-500 bg-emerald-50 p-2 text-xs text-emerald-900"
          data-testid="factuurdetail-afgerond-banner"
        >
          Opvolging is <strong>afgerond</strong>. Er worden geen reminders
          meer gepland of verzonden.
        </div>
      )}

      {/* Historie */}
      <div>
        <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">
          Herinneringshistorie ({(status.history || []).length})
        </div>
        <div
          className="border border-gray-200 max-h-64 overflow-y-auto"
          data-testid="reminder-history-list"
        >
          <table className="w-full text-[11px]">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="p-1.5 text-left">Datum</th>
                <th className="p-1.5 text-left">Fase</th>
                <th className="p-1.5 text-left">Status</th>
                <th className="p-1.5 text-left">Ontvanger</th>
                <th className="p-1.5 text-right">Openstaand snapshot</th>
                <th className="p-1.5 text-left">Reden/Fout</th>
              </tr>
            </thead>
            <tbody>
              {(status.history || []).map((row) => (
                <tr
                  key={row.id}
                  className="border-t border-gray-100"
                  data-testid={`reminder-history-row-${row.id}`}
                >
                  <td className="p-1.5 font-mono text-[10px]">
                    {row.sent_at || row.created_at || row.scheduled_date}
                  </td>
                  <td className="p-1.5">
                    {STAGE_LABELS[row.stage_key] || row.stage_key}
                  </td>
                  <td className="p-1.5">
                    <span className={
                      row.status === "sent" ? "text-emerald-700 font-bold" :
                      row.status === "failed" ? "text-red-700 font-bold" :
                      row.status === "cancelled" ? "text-gray-500" :
                      row.status === "paused" ? "text-amber-700" :
                      "text-blue-700"
                    }>{row.status}</span>
                  </td>
                  <td className="p-1.5 text-[10px]">{row.sent_to || "—"}</td>
                  <td className="p-1.5 text-right">
                    {row.amount_due_snapshot !== undefined && row.amount_due_snapshot !== null
                      ? fmt(row.amount_due_snapshot, currency) : "—"}
                  </td>
                  <td className="p-1.5 text-[10px] text-gray-600">
                    {row.reason_skipped || row.error || "—"}
                  </td>
                </tr>
              ))}
              {(status.history || []).length === 0 && (
                <tr>
                  <td colSpan={6} className="p-3 text-center text-gray-500 italic">
                    Nog geen reminders verzonden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ReminderPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        invoiceId={invoiceId}
        invoiceNumber={status.invoice_number}
        defaultStage="on_due"
        onSent={() => load()}
      />
      <ReminderPauseDialog
        open={pauseOpen}
        onOpenChange={setPauseOpen}
        invoiceId={invoiceId}
        defaultReason={pauseReason}
        onPaused={() => load()}
      />
    </div>
  );
}
